import React, { useState, useRef } from "react";
import { X, Mic, Image, MessageSquare, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface EchoMemoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  countryCode: string;
  cityId: string;
  cityName: string;
  sceneType: string;
  onSave?: () => void;
}

const EchoMemoryModal: React.FC<EchoMemoryModalProps> = ({
  isOpen,
  onClose,
  countryCode,
  cityId,
  cityName,
  sceneType,
  onSave,
}) => {
  const { user } = useAuthContext();
  const [memoryType, setMemoryType] = useState<"voice" | "photo" | "phrase" | "mood">("phrase");
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [phrase, setPhrase] = useState("");
  const [mood, setMood] = useState("");
  const [culturalExchange, setCulturalExchange] = useState("");
  const [saving, setSaving] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setRecording(true);
      setRecordingTime(0);

      timerRef.current = window.setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 10) {
            stopRecording();
            return 10;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Could not access microphone");
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  // Handle photo selection
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Save echo memory
  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      let voiceNoteUrl: string | undefined;
      let photoUrl: string | undefined;

      // Upload voice note if exists
      if (audioBlob && memoryType === "voice") {
        const fileName = `echo-voice-${Date.now()}.webm`;
        const { data, error } = await supabase.storage
          .from("profile-videos")
          .upload(`${user.id}/echo-memories/${fileName}`, audioBlob, {
            contentType: "audio/webm",
          });

        if (error) throw error;
        const { data: urlData } = supabase.storage.from("profile-videos").getPublicUrl(data.path);
        voiceNoteUrl = urlData.publicUrl;
      }

      // Upload photo if exists
      if (photoFile && memoryType === "photo") {
        const fileName = `echo-photo-${Date.now()}.jpg`;
        const { data, error } = await supabase.storage
          .from("profile-photos")
          .upload(`${user.id}/echo-memories/${fileName}`, photoFile, {
            contentType: photoFile.type,
          });

        if (error) throw error;
        const { data: urlData } = supabase.storage.from("profile-photos").getPublicUrl(data.path);
        photoUrl = urlData.publicUrl;
      }

      // Save to database (using a global_memories table or matches/pops with extended context)
      // For now, we'll extend the source_context of the most recent match/pop
      // In production, you'd create a dedicated global_memories table

      const memoryData = {
        user_id: user.id,
        memory_type: "echo",
        country_code: countryCode,
        city_id: cityId,
        city_name: cityName,
        scene_type: sceneType,
        voice_note_url: voiceNoteUrl,
        photo_url: photoUrl,
        shared_phrase: memoryType === "phrase" ? phrase : undefined,
        mood: memoryType === "mood" ? mood : undefined,
        cultural_exchange: culturalExchange || undefined,
        created_at: new Date().toISOString(),
      };

      // Store in localStorage for now (in production, use a database table)
      const existingMemories = JSON.parse(localStorage.getItem("global_memories") || "[]");
      existingMemories.push(memoryData);
      localStorage.setItem("global_memories", JSON.stringify(existingMemories));

      toast.success("Echo Memory saved! 🌍");
      onSave?.();
      onClose();
      resetForm();
    } catch (error) {
      console.error("Error saving echo memory:", error);
      toast.error("Failed to save memory");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setMemoryType("phrase");
    setRecording(false);
    setRecordingTime(0);
    setAudioBlob(null);
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhrase("");
    setMood("");
    setCulturalExchange("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Capture This Moment</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {cityName} • {sceneType}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Memory Type Selector */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setMemoryType("voice")}
              className={cn(
                "p-3 rounded-xl border transition-all text-left",
                memoryType === "voice" ? "border-primary bg-primary/5" : "border-border"
              )}
            >
              <Mic className="w-5 h-5 mb-2" />
              <div className="text-sm font-medium">Voice Note</div>
              <div className="text-xs text-muted-foreground">10 seconds</div>
            </button>
            <button
              onClick={() => setMemoryType("photo")}
              className={cn(
                "p-3 rounded-xl border transition-all text-left",
                memoryType === "photo" ? "border-primary bg-primary/5" : "border-border"
              )}
            >
              <Image className="w-5 h-5 mb-2" />
              <div className="text-sm font-medium">Photo</div>
              <div className="text-xs text-muted-foreground">Snapshot</div>
            </button>
            <button
              onClick={() => setMemoryType("phrase")}
              className={cn(
                "p-3 rounded-xl border transition-all text-left",
                memoryType === "phrase" ? "border-primary bg-primary/5" : "border-border"
              )}
            >
              <MessageSquare className="w-5 h-5 mb-2" />
              <div className="text-sm font-medium">Phrase</div>
              <div className="text-xs text-muted-foreground">Shared words</div>
            </button>
            <button
              onClick={() => setMemoryType("mood")}
              className={cn(
                "p-3 rounded-xl border transition-all text-left",
                memoryType === "mood" ? "border-primary bg-primary/5" : "border-border"
              )}
            >
              <span className="text-2xl mb-2 block">😊</span>
              <div className="text-sm font-medium">Mood</div>
              <div className="text-xs text-muted-foreground">Feeling</div>
            </button>
          </div>

          {/* Voice Recording */}
          {memoryType === "voice" && (
            <div className="space-y-3">
              {!audioBlob ? (
                <div className="flex items-center justify-center gap-4 p-6 rounded-xl bg-muted/40">
                  {recording ? (
                    <>
                      <div className="w-16 h-16 rounded-full bg-red-500 animate-pulse flex items-center justify-center">
                        <Mic className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold">{recordingTime}s / 10s</div>
                        <Button onClick={stopRecording} variant="outline" size="sm" className="mt-2">
                          Stop Recording
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Button onClick={startRecording} size="lg" className="gap-2">
                        <Mic className="w-5 h-5" />
                        Start Recording
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mic className="w-5 h-5 text-primary" />
                    <div>
                      <div className="text-sm font-medium">Voice note recorded</div>
                      <div className="text-xs text-muted-foreground">10 seconds</div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setAudioBlob(null)}>
                    Re-record
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Photo Upload */}
          {memoryType === "photo" && (
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="hidden"
              />
              {photoPreview ? (
                <div className="space-y-2">
                  <img src={photoPreview} alt="Preview" className="w-full h-48 object-cover rounded-xl" />
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full">
                    Change Photo
                  </Button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full p-8 rounded-xl border-2 border-dashed border-border hover:border-primary transition-colors flex flex-col items-center gap-2"
                >
                  <Image className="w-8 h-8 text-muted-foreground" />
                  <div className="text-sm font-medium">Tap to add photo</div>
                </button>
              )}
            </div>
          )}

          {/* Phrase Input */}
          {memoryType === "phrase" && (
            <Textarea
              placeholder="Share a phrase, quote, or moment..."
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              className="min-h-[100px]"
            />
          )}

          {/* Mood Input */}
          {memoryType === "mood" && (
            <Input
              placeholder="How are you feeling? (e.g., peaceful, energized, curious)"
              value={mood}
              onChange={(e) => setMood(e.target.value)}
            />
          )}

          {/* Cultural Exchange (optional) */}
          <div>
            <label className="text-sm font-medium mb-2 block">Cultural Exchange (optional)</label>
            <Textarea
              placeholder="What did you learn or share?"
              value={culturalExchange}
              onChange={(e) => setCulturalExchange(e.target.value)}
              className="min-h-[80px]"
            />
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving || (memoryType === "voice" && !audioBlob) || (memoryType === "photo" && !photoFile) || (memoryType === "phrase" && !phrase) || (memoryType === "mood" && !mood)}
            className="w-full gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Echo Memory
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EchoMemoryModal;

